import { Icon, Tag, Text } from '@billy/design-system'
import { memo, ReactElement, useCallback } from 'react'

import { getFileSizeLabel } from '../../../../utils/getFileSizeLabel'
import { ActionText } from '../ActionText'
import { variantStyles } from './constants/variantStyles'
import * as Styled from './styles'
import { AttachmentTagVariant } from './types/attachmentTagVariant'

const TAG_MAX_WIDTH = 265

export interface AttachmentTagProps {
  actionLabel?: string
  errorMessage?: string
  fileName: string
  fileSize?: number
  isActionClickable?: boolean
  onActionClick?: () => void
  onClose: () => void
  variant?: AttachmentTagVariant
}

export const AttachmentTag = memo(
  ({
    actionLabel,
    errorMessage,
    fileName,
    fileSize,
    isActionClickable = false,
    onActionClick,
    onClose,
    variant = 'neutral',
  }: AttachmentTagProps): ReactElement => {
    const fileSizeLabel = fileSize && getFileSizeLabel(fileSize)
    const fullFileLabel = [fileName, fileSizeLabel, errorMessage].filter(Boolean).join(' ')
    const attachmentTagMaxWidth = variant === 'error' ? 'none' : `${TAG_MAX_WIDTH}px`
    const { iconColor, iconName, textColor } = variantStyles[variant] || {}
    const shouldDisplayActionLabel = isActionClickable && !!actionLabel && !!onActionClick

    const handleActionClick = useCallback(() => onActionClick?.(), [onActionClick])
    const handleCloseClick = useCallback(() => onClose(), [onClose])

    return (
      <Tag
        color={variant === 'error' ? 'red' : 'neutral'}
        maxWidth={attachmentTagMaxWidth}
        type="subtle"
        onClose={handleCloseClick}
      >
        <Styled.AttachmentTagLabelWrapper>
          {iconName && <Icon color={iconColor} icon={iconName} size="sm" />}
          <Text as="span" color={textColor} title={fullFileLabel} truncate variant="bodySM" weight="regular">
            {fileName}
          </Text>
          {fileSizeLabel && (
            <Text as="span" color={textColor} nowrap variant="bodySM" weight="regular">
              {fileSizeLabel}
            </Text>
          )}
          {errorMessage && (
            <Text as="span" color={textColor} nowrap variant="bodySM" weight="regular">
              {errorMessage}
            </Text>
          )}
          {shouldDisplayActionLabel && <ActionText onClick={handleActionClick}>{actionLabel}</ActionText>}
        </Styled.AttachmentTagLabelWrapper>
      </Tag>
    )
  },
)
